import React from 'react';
import { NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { useCpsDialog } from '../../../common/useCpsDialog';
import { useConfigurationKeysManagementPermissions } from '../hooks/useConfigurationKeysManagementPermissions';
import {
  useGlobalizationContext,
  useTranslation,
  useGetMultiFileUploadLimit,
  useUpsertUiAdminConfigEntry,
  useGetIndexes
} from '@cs-cms/state-management';
import {
  CpsIndex,
  MULTI_FILE_UPLOAD_LIMIT_MAX,
  MULTI_FILE_UPLOAD_LIMIT_MIN,
  MULTI_FILE_UPLOAD_LIMIT_KEY_PREFIX,
  CpsUiAdminConfigType
} from '@cs-cms/types';
import { ConfigurationLimitStatusProps } from './ConfigurationLimitStatus';
import {
  findSelectedIndexDropdownOption,
  getIndexFromDropdownChange,
  getLimitFromNumericChange,
  isConfigurationSaveDisabled,
  mapIndexesToDropdownOptions
} from './configurationLimitUtils';

export interface MultiFileUploadLimitConfigViewModel {
  translate: (key: string) => string;
  DialogComponent: React.ReactNode;
  indexOptions: ReturnType<typeof mapIndexesToDropdownOptions>;
  selectedOption: ReturnType<typeof findSelectedIndexDropdownOption>;
  displayLimit: number;
  isLimitInputDisabled: boolean;
  limitStatusProps: ConfigurationLimitStatusProps;
  isSaveDisabled: boolean;
  saveButtonLabel: string;
  handleIndexChange: (event: DropDownListChangeEvent) => void;
  handleLimitChange: (event: NumericTextBoxChangeEvent) => void;
  handleSave: () => void;
}

export function useMultiFileUploadLimitConfig(): MultiFileUploadLimitConfigViewModel {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showDialog, DialogComponent } = useCpsDialog();
  const [localLimit, setLocalLimit] = React.useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState<CpsIndex | null>(null);
  const { data: currentLimit, isLoading, error } = useGetMultiFileUploadLimit(selectedIndex?.id);
  const upsertConfigMutation = useUpsertUiAdminConfigEntry();
  const { data: indexes } = useGetIndexes();

  const { canEditMultiFileUploadLimit, isLoadingPermissions } = useConfigurationKeysManagementPermissions(
    selectedIndex?.id
  );

  React.useEffect(() => {
    if (indexes && indexes.length > 0 && !selectedIndex) {
      setSelectedIndex(indexes[0]);
    }
  }, [indexes, selectedIndex]);

  const indexOptions = mapIndexesToDropdownOptions(indexes);
  const selectedOption = findSelectedIndexDropdownOption(selectedIndex, indexOptions);
  const displayLimit = localLimit ?? currentLimit ?? MULTI_FILE_UPLOAD_LIMIT_MAX;
  const hasUnsavedChanges = localLimit !== null && localLimit !== currentLimit;
  const isSaving = upsertConfigMutation.isPending;

  const persistLimit = async (): Promise<void> => {
    if (!selectedIndex || localLimit === null) {
      return;
    }

    const configKey = `${MULTI_FILE_UPLOAD_LIMIT_KEY_PREFIX}${selectedIndex.id}` as CpsUiAdminConfigType;

    await upsertConfigMutation.mutateAsync({
      key: configKey,
      body: String(localLimit)
    });

    setLocalLimit(null);
  };

  const handleIndexChange = (event: DropDownListChangeEvent): void => {
    const index = getIndexFromDropdownChange(event);

    if (!index) {
      return;
    }

    setSelectedIndex(index);
    setLocalLimit(null);
  };

  const handleLimitChange = (event: NumericTextBoxChangeEvent): void => {
    setLocalLimit(getLimitFromNumericChange(event, MULTI_FILE_UPLOAD_LIMIT_MIN, MULTI_FILE_UPLOAD_LIMIT_MAX));
  };

  const handleSave = (): void => {
    if (!selectedIndex || localLimit === null) {
      return;
    }

    showDialog({
      title: translate('custom.common.saveChangesTitle'),
      message: translate('custom.settings.adminCenter.configurations.saveChangesMessage'),
      confirmButtonLabel: translate('custom.common.save'),
      discardButtonLabel: translate('custom.common.cancel'),
      onConfirm: async confirmed => {
        if (confirmed) {
          await persistLimit();
        }
      }
    });
  };

  const canEdit = canEditMultiFileUploadLimit === true;

  const limitStatusProps: ConfigurationLimitStatusProps = {
    isLoading,
    isLoadingPermissions,
    error,
    canEdit,
    selectedIndex,
    currentLimit,
    hasUnsavedChanges,
    translate
  };

  return {
    translate,
    DialogComponent,
    indexOptions,
    selectedOption,
    displayLimit,
    isLimitInputDisabled: isLoading || isSaving || !canEdit,
    limitStatusProps,
    isSaveDisabled: isConfigurationSaveDisabled({
      hasSelectedIndex: selectedIndex !== null,
      hasUnsavedChanges,
      isLoading,
      isSaving,
      canEdit
    }),
    saveButtonLabel: isSaving
      ? translate('custom.settings.adminCenter.configurations.saving')
      : translate('custom.settings.adminCenter.configurations.save'),
    handleIndexChange,
    handleLimitChange,
    handleSave
  };
}
